import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface NavLink {
  label: string;
  href: string;
  /** External link opens in new tab */
  external?: boolean;
}

export interface NavigationProps {
  /** Navigation links */
  links?: NavLink[];
  /** Additional CSS classes */
  className?: string;
}

const defaultNavLinks: NavLink[] = [
  { label: 'About', href: '/about' },
  { label: 'Projects', href: '/projects' },
  { label: 'AI Help', href: '/ai-help' },
  { label: 'Contact', href: '/contact' },
];

const mobileMenuVariants = {
  hidden: {
    x: '100%',
    transition: {
      duration: 0.3,
      ease: 'easeInOut',
    },
  },
  visible: {
    x: '0%',
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
};

const mobileLinkVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.3, delay: i * 0.08 },
  }),
};

/**
 * Navigation Component (Dark Theme - Kelley SD Pivot)
 *
 * Minimal dark navigation with "KELLEY SD" wordmark in Space Grotesk 600.
 * Sticky with backdrop-blur on near-black background.
 * Mobile hamburger with dark slide-out panel.
 * Active route indicated via accent-amber.
 *
 * @example
 * ```tsx
 * <Navigation />
 * <Navigation links={[{ label: 'About', href: '/about' }]} />
 * ```
 */
export function Navigation({
  links = defaultNavLinks,
  className,
}: NavigationProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu on escape key + lock body scroll
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const toggleMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
  }, []);

  const isActiveLink = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <header
      className={cn(
        'sticky top-0 z-40 w-full',
        'bg-bg-primary/80 backdrop-blur-sm',
        'border-b border-border-subtle',
        className
      )}
      role="banner"
    >
      <div className="mx-auto max-w-container-xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo / Wordmark */}
          <Link
            to="/"
            className={cn(
              'flex items-center transition-opacity duration-200',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary rounded-md',
              'hover:opacity-80'
            )}
            aria-label="Kelley SD - Home"
          >
            <span className="font-display text-lg font-semibold tracking-[0.15em] uppercase text-text-primary">
              KELLEY SD
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:block" aria-label="Main navigation">
            <ul className="flex items-center gap-8">
              {links.map((link) => {
                const isActive = isActiveLink(link.href);
                return (
                  <li key={link.href}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                          'text-sm font-body font-medium tracking-wide transition-colors duration-200',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary rounded-sm',
                          'text-text-secondary hover:text-text-primary'
                        )}
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className={cn(
                          'relative text-sm font-body font-medium tracking-wide transition-colors duration-200',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary rounded-sm',
                          isActive
                            ? 'text-accent-amber'
                            : 'text-text-secondary hover:text-text-primary'
                        )}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        {link.label}
                        {isActive && (
                          <motion.span
                            layoutId="nav-active-indicator"
                            className="absolute -bottom-1 left-0 right-0 h-[2px] bg-accent-amber"
                            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                          />
                        )}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className={cn(
              'md:hidden p-2 rounded-lg',
              'text-text-secondary hover:text-text-primary',
              'transition-colors duration-200',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber'
            )}
            onClick={toggleMenu}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Slide-Out Panel */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/60 md:hidden z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              aria-hidden="true"
            />

            {/* Slide-out panel */}
            <motion.div
              id="mobile-menu"
              className="fixed top-0 right-0 bottom-0 w-72 bg-bg-surface md:hidden z-50 border-l border-border-subtle"
              variants={mobileMenuVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              {/* Close button inside panel */}
              <div className="flex items-center justify-between px-6 h-16 border-b border-border-subtle">
                <span className="font-display text-sm font-semibold tracking-[0.15em] uppercase text-text-primary">
                  KELLEY SD
                </span>
                <button
                  type="button"
                  className="p-2 text-text-secondary hover:text-text-primary transition-colors rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber"
                  onClick={() => setIsMobileMenuOpen(false)}
                  aria-label="Close menu"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Mobile links */}
              <nav aria-label="Mobile navigation" className="px-6 py-8">
                <ul className="space-y-2">
                  {links.map((link, i) => {
                    const isActive = isActiveLink(link.href);
                    return (
                      <motion.li
                        key={link.href}
                        custom={i}
                        variants={mobileLinkVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        {link.external ? (
                          <a
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cn(
                              'block py-3 px-4 text-base font-body font-medium rounded-lg transition-colors duration-200',
                              'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
                            )}
                          >
                            {link.label}
                          </a>
                        ) : (
                          <Link
                            to={link.href}
                            className={cn(
                              'block py-3 px-4 text-base font-body font-medium rounded-lg transition-colors duration-200',
                              isActive
                                ? 'text-accent-amber bg-bg-elevated'
                                : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
                            )}
                            aria-current={isActive ? 'page' : undefined}
                          >
                            {link.label}
                          </Link>
                        )}
                      </motion.li>
                    );
                  })}
                </ul>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}

export default Navigation;
